import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  Box,
  Text,
  Button,
  Badge,
  Icon,
  IconButton,
  Tooltip,
  VStack,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  ArrowLeft,
  FilePdf,
  Folder,
  Export,
  PaperPlaneTilt,
  ClockCounterClockwise,
} from 'phosphor-react';
import { IDossieHeaderProps } from '../utils/dossieTypes';
import { formatCNPJ } from '../../../../utils/FormatFunctions';
import { useAuth } from '../../../../providers/auth';

const GRUPO_ADMIN_ID = 1;

export function DossieHeader({
  companyInfo,
  onExportPDF,
  isExporting,
  onSendToClient,
  onViewLogs,
}: IDossieHeaderProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isAdmin =
    user && (Number(user.grupo_id) === GRUPO_ADMIN_ID || user.grupo_id === GRUPO_ADMIN_ID);

  const getRegimeBadgeColor = (regime: string) => {
    if (!regime) return 'gray';
    const normalizedRegime = regime.toLowerCase();
    if (normalizedRegime.includes('simples')) return 'green';
    if (normalizedRegime.includes('presumido')) return 'blue';
    if (normalizedRegime.includes('real')) return 'purple';
    return 'gray';
  };

  return (
    <Box mb={4}>
      <Flex justify="space-between" align="flex-start" gap={2}>
        <Flex align="flex-start" gap={2} flex={1} minW={0}>
          <Tooltip label="Voltar">
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              flexShrink={0}
            />
          </Tooltip>
          <Icon as={Folder} boxSize={10} color="blue.500" flexShrink={0} mt={1} />
          <VStack align="flex-start" spacing={0} minW={0} flex={1}>
            <Text fontSize="sm" fontWeight="bold" color="gray.800">
              Dossiê da Empresa
            </Text>
            {companyInfo && (
              <>
                <Text fontSize="xs" fontWeight="semibold" color="gray.700" noOfLines={1}>
                  {companyInfo.razao_social || companyInfo.nome}
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge
                    colorScheme={getRegimeBadgeColor(companyInfo.regime_tributario_atualizado)}
                    fontSize="xs"
                  >
                    {companyInfo.regime_tributario_atualizado || 'N/D'}
                  </Badge>
                  <Text fontSize="xs" color="gray.500">
                    {formatCNPJ(companyInfo.cnpj)}
                  </Text>
                </HStack>
              </>
            )}
          </VStack>
        </Flex>

        {/* Botões de exportação */}
        {isMobile ? (
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Exportar"
              icon={<Export size={18} />}
              size="sm"
              variant="outline"
              colorScheme="blue"
              isLoading={isExporting}
              isDisabled={!companyInfo}
            />
            <MenuList>
              <MenuItem icon={<FilePdf size={18} />} onClick={onExportPDF}>
                Exportar PDF
              </MenuItem>
              {isAdmin && (
                <MenuItem icon={<PaperPlaneTilt size={18} />} onClick={onSendToClient}>
                  Enviar ao Cliente
                </MenuItem>
              )}
              <MenuItem icon={<ClockCounterClockwise size={18} />} onClick={onViewLogs}>
                Ver Acessos
              </MenuItem>
            </MenuList>
          </Menu>
        ) : (
          <HStack spacing={2} flexShrink={0}>
            <Tooltip label="Ver acessos aos links">
              <IconButton
                aria-label="Ver acessos"
                icon={<ClockCounterClockwise size={16} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={onViewLogs}
                isDisabled={!companyInfo}
              />
            </Tooltip>
            <Button
              leftIcon={<FilePdf size={16} />}
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={onExportPDF}
              isLoading={isExporting}
              isDisabled={!companyInfo}
            >
              PDF
            </Button>
            {isAdmin && (
              <Button
                leftIcon={<PaperPlaneTilt size={16} />}
                size="sm"
                colorScheme="blue"
                onClick={onSendToClient}
                isDisabled={!companyInfo}
              >
                Enviar
              </Button>
            )}
          </HStack>
        )}
      </Flex>
    </Box>
  );
}
